import { zodResolver } from '@hookform/resolvers/zod';
import { isApiError } from '@saay/api-client';
import { Alert, AlertDescription, AlertTitle } from '@saay/shadcn/components/alert';
import {
  applyFieldErrors,
  Banner,
  Button,
  Checkbox,
  CommandFeedback,
  Dialog,
  FieldErrorList,
  NativeSelect,
  Skeleton,
  SolarDateInput,
  StatusChip,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { z } from 'zod';

import {
  formatCount,
  MOBILE_E164_PATTERN,
  normalizeCodeInput,
  normalizeMobileField,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';

import type {
  BuyerProvision,
  BuyerProvisioned,
  BuyerProvisioningPolicyVersion,
  GuaranteePolicyVersion,
} from '../api.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { ADMIN_FORM_OPTIONS, ResolvedChoice, useSubmitBlocked } from '../components/form.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { MobileField, NationalCodeField } from '../components/masked-fields.js';
import { GeographyFields } from '../components/pickers.js';
import { canPerform } from '../permissions.js';
import { setupKeys, useBuyers, useCurrentPolicy } from '../queries.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { tehranToday } from '../validation.js';
import type { BuyersOutletContext } from './buyers-list.js';

const text = (max: number) =>
  z.string().trim().min(1, 'این مورد الزامی است.').max(max, `حداکثر ${max} کاراکتر مجاز است.`);

/** Mirrors AC §17.5.5 (D-21, D-43); the checksum is a hint only, the server decides. */
export const provisionSchema = z.object({
  birthDate: z
    .string()
    .min(1, 'تاریخ تولد را وارد کنید.')
    .refine((value) => value < tehranToday(), 'تاریخ تولد باید پیش از امروز باشد.'),
  cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
  firstNameFa: text(100),
  identityDocument: z.object({
    note: z.string().trim().max(500, 'حداکثر ۵۰۰ کاراکتر.'),
    type: z.string().min(1, 'نوع مدرک را انتخاب کنید.'),
    verified: z.boolean(),
  }),
  lastNameFa: text(100),
  mobileE164: z
    .string()
    .transform(normalizeMobileField)
    .refine(
      (value) => MOBILE_E164_PATTERN.test(value),
      'شماره موبایل را مانند ۰۹۱۲۱۲۳۴۵۶۷ وارد کنید.',
    ),
  nationalCode: z
    .string()
    .transform(normalizeCodeInput)
    .refine((value) => /^\d{10}$/.test(value), 'کد ملی ۱۰ رقم است.'),
  provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
  sendLoginInvitation: z.boolean(),
  guarantorCodes: z.array(z.string().trim().min(1, 'کد معرف را وارد کنید.')),
});
type ProvisionInput = z.input<typeof provisionSchema>;
type ProvisionValues = z.output<typeof provisionSchema>;

const BASE_FIELDS = [
  'nationalCode',
  'firstNameFa',
  'lastNameFa',
  'birthDate',
  'mobileE164',
  'provinceId',
  'cityId',
  'identityDocument.type',
  'identityDocument.verified',
  'identityDocument.note',
  'sendLoginInvitation',
];

function guarantorFieldPaths(count: number): string[] {
  return Array.from({ length: count }, (_, index) => `guarantorCodes.${index}`);
}

export function toBuyerProvision(
  values: ProvisionValues,
  requiredGuarantorCount = 0,
): BuyerProvision {
  const codes = values.guarantorCodes.map((code) => code.trim()).filter((code) => code !== '');
  return {
    birthDate: values.birthDate,
    cityId: values.cityId,
    firstNameFa: values.firstNameFa,
    guarantorCodes: requiredGuarantorCount === 0 ? [] : codes,
    identityDocument: {
      note: values.identityDocument.note === '' ? null : values.identityDocument.note,
      type: values.identityDocument.type,
      verified: values.identityDocument.verified,
    },
    lastNameFa: values.lastNameFa,
    mobileE164: values.mobileE164,
    nationalCode: values.nationalCode,
    provinceId: values.provinceId,
    sendLoginInvitation: values.sendLoginInvitation,
  };
}

function BuyerDuplicateHint({
  label,
  match,
}: {
  readonly label: string;
  readonly match: {
    readonly buyerId: string;
    readonly firstNameFa: string;
    readonly lastNameFa: string;
  };
}) {
  return (
    <p className="admin-duplicate-hint">
      {`${label} برای ${match.firstNameFa} ${match.lastNameFa} ثبت شده · `}
      <Link state={OPENED_FROM_LIST} to={`/buyers/${match.buyerId}`}>
        باز کردن
      </Link>
    </p>
  );
}

function ProvisionForm({
  guarantee,
  policy,
}: {
  readonly guarantee: GuaranteePolicyVersion | undefined;
  readonly policy: BuyerProvisioningPolicyVersion;
}) {
  const requiredGuarantorCount = guarantee?.requiredDirectGuarantorCount ?? 0;
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const [existingBuyerId, setExistingBuyerId] = useState<string | null>(null);
  const documentTypes = policy.acceptedIdentityDocumentTypes;
  const form = useForm<ProvisionInput, unknown, ProvisionValues>({
    ...ADMIN_FORM_OPTIONS,
    defaultValues: {
      birthDate: '',
      cityId: '',
      firstNameFa: '',
      identityDocument: {
        note: '',
        type: documentTypes.length === 1 ? documentTypes[0]! : '',
        verified: false,
      },
      lastNameFa: '',
      mobileE164: '',
      nationalCode: '',
      provinceId: '',
      sendLoginInvitation: policy.sendLoginInvitationByDefault,
      guarantorCodes: Array.from({ length: requiredGuarantorCount }, () => ''),
    },
    resolver: zodResolver(
      provisionSchema.superRefine((values, context) => {
        if (requiredGuarantorCount === 0) {
          return;
        }
        if (values.guarantorCodes.length !== requiredGuarantorCount) {
          context.addIssue({
            code: 'custom',
            message: `${formatCount(requiredGuarantorCount)} کد معرف ضامن لازم است.`,
            path: ['guarantorCodes'],
          });
        }
      }),
    ),
  });
  const fields = [...BASE_FIELDS, ...guarantorFieldPaths(requiredGuarantorCount)];
  const command = useCommand<BuyerProvision, BuyerProvisioned>(
    'buyer.provision',
    (client, body, intent) => client.post('/admin/buyers', { body, intent }),
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.buyers });
        toast.success({ confirmedBy: result, title: 'خریدار ثبت شد' });
        navigate(`/buyers/${result.data.buyerId}`, { replace: true, state: OPENED_FROM_LIST });
      },
    },
  );

  const nationalDigits = normalizeCodeInput(form.watch('nationalCode'));
  const mobileDigits = normalizeMobileField(form.watch('mobileE164'));
  const nationalComplete = /^\d{10}$/.test(nationalDigits);
  const mobileComplete = MOBILE_E164_PATTERN.test(mobileDigits);
  const nationalLookup = useBuyers({ nationalCode: nationalDigits }, nationalComplete);
  const mobileLookup = useBuyers({ mobileE164: mobileDigits }, mobileComplete);
  // An empty field shares the unfiltered list's cache key: W4 saw «این کد ملی برای رضا صادقی ثبت
  // شده» on an empty form, so a hint needs a complete value.
  const nationalMatch = nationalComplete ? nationalLookup.data?.pages[0]?.items[0] : undefined;
  const mobileMatch = mobileComplete ? mobileLookup.data?.pages[0]?.items[0] : undefined;

  function handle(outcome: CommandOutcome<BuyerProvisioned>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(applyFieldErrors(error, fields, form.setError));
    if (error.code === 'NATIONAL_CODE_ALREADY_REGISTERED') {
      form.setError('nationalCode', { message: error.messageFa, type: 'server' });
    }
    if (error.code === 'BUYER_ALREADY_EXISTS') {
      form.setError('mobileE164', { message: error.messageFa, type: 'server' });
      const buyerId = error.details?.buyerId;
      setExistingBuyerId(typeof buyerId === 'string' ? buyerId : null);
    }
    if (error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError('cityId', { message: error.messageFa, type: 'server' });
    }
  }

  async function submit(values: ProvisionValues) {
    setUnmapped([]);
    setExistingBuyerId(null);
    handle(await command.submit(toBuyerProvision(values, requiredGuarantorCount)));
  }

  const errors = form.formState.errors;
  const verified = form.watch('identityDocument.verified');
  const submitLabel = verified ? 'ثبت خریدار فعال' : 'ثبت در انتظار تأیید هویت';

  return (
    <form
      aria-label="ثبت خریدار"
      className="admin-form"
      noValidate
      onSubmit={form.handleSubmit(submit)}
    >
      <fieldset className="admin-fieldset">
        <legend>هویت</legend>
        <Controller
          control={form.control}
          name="nationalCode"
          render={({ field, fieldState }) => (
            <NationalCodeField
              error={fieldState.error?.message}
              label="کد ملی"
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        {nationalMatch === undefined ? null : (
          <BuyerDuplicateHint label="این کد ملی" match={nationalMatch} />
        )}
        <div className="admin-grid-2">
          <TextField
            error={errors.firstNameFa?.message}
            label="نام"
            {...form.register('firstNameFa')}
          />
          <TextField
            error={errors.lastNameFa?.message}
            label="نام خانوادگی"
            {...form.register('lastNameFa')}
          />
        </div>
        <Controller
          control={form.control}
          name="birthDate"
          render={({ field, fieldState }) => (
            <AdminSolarDateField
              error={fieldState.error?.message}
              label="تاریخ تولد"
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
      </fieldset>
      <fieldset className="admin-fieldset">
        <legend>تماس و محل سکونت</legend>
        <Controller
          control={form.control}
          name="mobileE164"
          render={({ field, fieldState }) => (
            <MobileField
              error={fieldState.error?.message}
              label="شماره موبایل"
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        {mobileMatch === undefined ? null : (
          <BuyerDuplicateHint label="این شماره موبایل" match={mobileMatch} />
        )}
        <Controller
          control={form.control}
          name="provinceId"
          render={({ field: province }) => (
            <Controller
              control={form.control}
              name="cityId"
              render={({ field: city }) => (
                <GeographyFields
                  cityError={errors.cityId?.message}
                  cityId={city.value}
                  idPrefix="buyer"
                  onCityChange={city.onChange}
                  onProvinceChange={province.onChange}
                  provinceError={errors.provinceId?.message}
                  provinceId={province.value}
                />
              )}
            />
          )}
        />
      </fieldset>
      <fieldset className="admin-fieldset">
        <legend>مدرک شناسایی</legend>
        {documentTypes.length === 1 ? (
          <ResolvedChoice
            label="نوع مدرک"
            value={
              statusLabel('identityDocumentType', documentTypes[0]!) === 'نامشخص'
                ? documentTypes[0]!
                : statusLabel('identityDocumentType', documentTypes[0]!)
            }
          />
        ) : (
          <NativeSelect
            error={errors.identityDocument?.type?.message}
            id="buyer-document-type"
            label="نوع مدرک"
            options={documentTypes.map((value) => ({
              label:
                statusLabel('identityDocumentType', value) === 'نامشخص'
                  ? value
                  : statusLabel('identityDocumentType', value),
              value,
            }))}
            placeholder="انتخاب نوع مدرک"
            {...form.register('identityDocument.type')}
          />
        )}
        <Checkbox
          error={errors.identityDocument?.verified?.message}
          label="مدرک را دیدم و با مشخصات بالا تطبیق دادم"
          {...form.register('identityDocument.verified')}
        />
        <TextArea
          error={errors.identityDocument?.note?.message}
          label="یادداشت بررسی (اختیاری)"
          {...form.register('identityDocument.note')}
        />
      </fieldset>
      {requiredGuarantorCount === 0 ? null : (
        <fieldset className="admin-fieldset">
          <legend>کدهای معرف ضامن</legend>
          <p className="admin-muted">
            {`طبق سیاست ضمانت، ${formatCount(requiredGuarantorCount)} کد معرف معتبر لازم است.`}
          </p>
          {Array.from({ length: requiredGuarantorCount }, (_, index) => (
            <TextField
              autoComplete="off"
              error={errors.guarantorCodes?.[index]?.message}
              key={index}
              label={`کد معرف ${toPersianDigits(index + 1)}`}
              ltr
              {...form.register(`guarantorCodes.${index}` as const)}
            />
          ))}
        </fieldset>
      )}
      <Checkbox label="ارسال پیامک دعوت به ورود" {...form.register('sendLoginInvitation')} />
      <FieldErrorList errors={unmapped} />
      {existingBuyerId === null ? null : (
        <p>
          <Link to={`/buyers/${existingBuyerId}`}>مشاهده خریدار موجود</Link>
        </p>
      )}
      <CommandFeedback
        onCheckStatus={() => void command.checkStatus().then(handle)}
        pendingLabel="در حال ثبت خریدار…"
        state={command.state}
      />
      <div className="admin-actions admin-actions--end">
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          {submitLabel}
        </Button>
      </div>
      {offline.reason === undefined ? null : <p className="admin-muted">{offline.reason}</p>}
    </form>
  );
}

/**
 * `/buyers/new`: the prototype buyer sheet (`buyerCreateSheet`) over the buyers list. A direct load
 * renders the list underneath; closing returns to it.
 */
export function BuyerProvisionSheet() {
  const { self } = useOutletContext<BuyersOutletContext>();
  const close = useCloseSheet('/buyers');
  usePageTitle('ساخت حساب خریدار');
  return (
    <Dialog onOpenChange={onSheetOpenChange(close)} open title="ساخت حساب خریدار">
      {canPerform(self, 'provisionBuyer') ? <ProvisionWithPolicy /> : <NotAllowed />}
    </Dialog>
  );
}

function NotAllowed() {
  return (
    <Alert>
      <AlertTitle>
        <h2>به این بخش دسترسی ندارید</h2>
      </AlertTitle>
      <AlertDescription>نقش شما اجازه ثبت خریدار را ندارد.</AlertDescription>
    </Alert>
  );
}

function ProvisionWithPolicy() {
  const policy = useCurrentPolicy<BuyerProvisioningPolicyVersion>('buyer-provisioning');
  const guarantee = useCurrentPolicy<GuaranteePolicyVersion>('guarantee');
  const loading = policy.status === 'pending' || guarantee.status === 'pending';
  const error = policy.error ?? guarantee.error;
  return (
    <QueryState
      error={error}
      onRetry={() => {
        void policy.refetch();
        void guarantee.refetch();
      }}
      skeleton={<Skeleton label="در حال بارگذاری سیاست ثبت خریدار…" lines={6} />}
      status={loading ? 'pending' : policy.status}
    >
      {policy.data === undefined ? null : (
        <ProvisionForm guarantee={guarantee.data} policy={policy.data} />
      )}
    </QueryState>
  );
}
